import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useSarLabAuth } from './SarLabAuthContext';
import { supportApi, SupportTicket as ApiTicket, SupportMessage as ApiMessage } from '../services/api';
import { supportWebSocket, WebSocketMessage } from '../services/websocketService';

interface SupportMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: 'user' | 'admin';
  timestamp: number;
}

interface SupportTicket {
  id: string;
  odaId: string;
  odaAdi: string;
  userId: string;
  username: string;
  subject: string;
  initialMessage: string;
  status: 'waiting' | 'active' | 'closed';
  createdAt: number;
  assignedAdmin?: string;
  assignedAdminName?: string;
}

interface LiveSupportContextType {
  activeTicket: SupportTicket | null;
  tickets: SupportTicket[];
  messages: SupportMessage[];
  isConnected: boolean;
  isLoadingTicket: boolean;
  typingUser: string | null;
  createTicket: (subject: string, initialMessage: string) => Promise<string>;
  sendSupportMessage: (text: string) => Promise<void>;
  closeTicket: (saveToHistory?: boolean) => Promise<void>;
  joinTicket: (ticketId: string) => Promise<void>;
  leaveTicket: () => void;
  adminCloseTicket: (ticketId: string) => Promise<void>;
  loadMessages: (ticketId: string) => Promise<void>;
  sendTyping: () => void;
  stopTyping: () => void;
}

const LiveSupportContext = createContext<LiveSupportContextType>({} as LiveSupportContextType);

export const useLiveSupport = () => {
  return useContext(LiveSupportContext);
};

const convertApiTicket = (apiTicket: ApiTicket, username: string): SupportTicket => ({
  id: apiTicket.id,
  odaId: apiTicket.odaId || apiTicket.id,
  odaAdi: apiTicket.odaAdi || apiTicket.subject,
  userId: '',
  username: username,
  subject: apiTicket.subject,
  initialMessage: apiTicket.initialMessage || '',
  status: apiTicket.status,
  createdAt: new Date(apiTicket.createdAt).getTime(),
  assignedAdminName: apiTicket.assignedAdminName,
});

const convertApiMessage = (apiMsg: ApiMessage, playerUsername?: string): SupportMessage => {
  // Backend returns senderRole, but some places might use senderType
  const role = apiMsg.senderRole || apiMsg.senderType;
  const isPlayer = role === 'player';

  return {
    id: apiMsg.id,
    text: apiMsg.message || apiMsg.content || '',
    senderId: apiMsg.senderId,
    senderName: isPlayer ? (apiMsg.senderName || playerUsername || 'Oyuncu') : (apiMsg.senderName || 'Destek'),
    senderRole: isPlayer ? 'user' : 'admin',
    timestamp: new Date(apiMsg.createdAt).getTime(),
  };
};

interface LiveSupportProviderProps {
  children: ReactNode;
}

export const LiveSupportProvider: React.FC<LiveSupportProviderProps> = ({ children }) => {
  const { currentPlayer, isLoggedIn } = useSarLabAuth();
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleWsMessage = useCallback((message: WebSocketMessage) => {
    console.log('[LiveSupport] WS message:', message.type);

    switch (message.type) {
      case 'auth_success':
        console.log('[LiveSupport] WS Authenticated');
        setWsConnected(true);
        break;

      case 'error':
        console.error('[LiveSupport] WS Error:', message.error);
        break;

      case 'joined_ticket':
        console.log('[LiveSupport] Joined ticket:', message.ticketId);
        break;

      case 'new_message':
        const msgData = message.message as { id: string; content?: string; message?: string; senderId: string; senderName: string; senderType?: string; senderRole?: string; createdAt: string } | undefined;
        if (msgData) {
          const messageText = msgData.message || msgData.content || '';
          // Support both senderType (WebSocket) and senderRole (API)
          const role = msgData.senderRole || msgData.senderType;
          const isPlayer = role === 'player';
          const playerName = currentPlayer?.username || 'Oyuncu';
          const newMsg: SupportMessage = {
            id: msgData.id || Date.now().toString(),
            text: messageText,
            senderId: msgData.senderId,
            senderName: isPlayer ? (msgData.senderName || playerName) : (msgData.senderName || 'Destek'),
            senderRole: isPlayer ? 'user' : 'admin',
            timestamp: new Date(msgData.createdAt).getTime(),
          };
          setMessages(prev => {
            if (prev.some(m => m.id === newMsg.id)) {
              return prev;
            }
            return [...prev, newMsg];
          });
        }
        break;

      case 'typing':
        setTypingUser((message.username as string) || 'Destek');
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          setTypingUser(null);
        }, 3000);
        break;

      case 'stop_typing':
        setTypingUser(null);
        break;

      case 'ticket_closed':
        if (activeTicket && message.ticketId === activeTicket.id) {
          setActiveTicket(prev => prev ? { ...prev, status: 'closed' } : null);
        }
        break;

      case 'disconnected':
        setWsConnected(false);
        break;

      case 'pong':
        break;
    }
  }, [activeTicket]);

  useEffect(() => {
    if (isLoggedIn) {
      supportWebSocket.connect()
        .then(() => {
          console.log('[LiveSupport] WebSocket connected');
        })
        .catch(err => {
          console.error('[LiveSupport] WebSocket connection failed:', err);
        });

      const unsubscribe = supportWebSocket.onMessage(handleWsMessage);

      return () => {
        unsubscribe();
        supportWebSocket.disconnect();
        setWsConnected(false);
      };
    } else {
      supportWebSocket.disconnect();
      setWsConnected(false);
    }
  }, [isLoggedIn, handleWsMessage]);

  useEffect(() => {
    if (isLoggedIn && currentPlayer) {
      loadTickets();
    } else {
      setTickets([]);
      setActiveTicket(null);
      setMessages([]);
    }
  }, [isLoggedIn, currentPlayer]);

  useEffect(() => {
    if (activeTicket && wsConnected) {
      supportWebSocket.joinTicket(activeTicket.id);
    }
  }, [activeTicket?.id, wsConnected]);

  const loadTickets = async () => {
    try {
      const result = await supportApi.getTickets();
      console.log('[LiveSupport] loadTickets result:', result);

      if (result.success && result.data) {
        const ticketData = result.data.ticket || result.data.tickets;

        if (ticketData) {
          const ticketsArray = Array.isArray(ticketData) ? ticketData : [ticketData];
          const convertedTickets = ticketsArray.map(t =>
            convertApiTicket(t, currentPlayer?.username || '')
          );
          setTickets(convertedTickets);

          const openTicket = ticketsArray.find(t => t.status === 'waiting' || t.status === 'active');
          if (openTicket) {
            setActiveTicket(convertApiTicket(openTicket, currentPlayer?.username || ''));
            await loadMessages(openTicket.id);
          }
        }
      }
    } catch (error) {
      console.error('Ticket listesi yuklenemedi:', error);
    }
  };

  const loadMessages = async (ticketId: string) => {
    try {
      const result = await supportApi.getTicketMessages(ticketId);
      if (result.success && result.data?.messages) {
        const username = currentPlayer?.username;
        setMessages(result.data.messages.map(msg => convertApiMessage(msg, username)));
      }
    } catch (error) {
      console.error('Mesajlar yuklenemedi:', error);
    }
  };

  const createTicket = async (subject: string, initialMessage: string): Promise<string> => {
    if (!currentPlayer) throw new Error('Kullanici girisi gerekli');

    setIsLoadingTicket(true);
    try {
      const result = await supportApi.createTicket(subject, initialMessage);
      if (result.success && result.data?.ticket) {
        const newTicket = convertApiTicket(result.data.ticket, currentPlayer.username);
        setActiveTicket(newTicket);
        setTickets(prev => [...prev, newTicket]);

        setMessages([{
          id: Date.now().toString(),
          text: initialMessage,
          senderId: currentPlayer.id,
          senderName: currentPlayer.username,
          senderRole: 'user',
          timestamp: Date.now(),
        }]);

        if (wsConnected) {
          supportWebSocket.joinTicket(result.data.ticket.id);
          // Notify admins about the new ticket
          supportWebSocket.notifyTicketCreated({
            id: result.data.ticket.id,
            subject: result.data.ticket.subject,
            subjectLabel: result.data.ticket.subjectLabel || result.data.ticket.subject,
          });
        }

        return result.data.ticket.id;
      }
      const errorMsg = result.error || 'Ticket olusturulamadi';
      console.error('Ticket creation failed:', result);
      throw new Error(errorMsg);
    } finally {
      setIsLoadingTicket(false);
    }
  };

  const sendSupportMessage = async (text: string): Promise<void> => {
    if (!currentPlayer || !activeTicket) return;

    const tempId = Date.now().toString();
    const localMessage: SupportMessage = {
      id: tempId,
      text,
      senderId: currentPlayer.id,
      senderName: currentPlayer.username,
      senderRole: 'user',
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, localMessage]);

    try {
      const result = await supportApi.sendTicketMessage(activeTicket.id, text);

      if (result.success && result.data?.message) {
        setMessages(prev => prev.map(m =>
          m.id === tempId ? convertApiMessage(result.data!.message, currentPlayer?.username) : m
        ));

        if (wsConnected) {
          supportWebSocket.sendMessage(text, result.data.message.id);
        }
      }
    } catch (error) {
      console.error('Mesaj gonderilemedi:', error);
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }
  };

  const closeTicket = async (_saveToHistory: boolean = true): Promise<void> => {
    const ticketToClose = activeTicket;
    supportWebSocket.leaveTicket();
    setActiveTicket(null);
    setMessages([]);

    // Remove closed ticket from tickets list so it won't be auto-selected again
    if (ticketToClose) {
      setTickets(prev => prev.filter(t => t.id !== ticketToClose.id));
    }
  };

  const joinTicket = async (ticketId: string): Promise<void> => {
    if (!currentPlayer) return;

    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket) {
      setActiveTicket(ticket);
      await loadMessages(ticketId);

      if (wsConnected) {
        supportWebSocket.joinTicket(ticketId);
      }
    }
  };

  const leaveTicket = () => {
    supportWebSocket.leaveTicket();
    setActiveTicket(null);
    setMessages([]);
  };

  const adminCloseTicket = async (_ticketId: string): Promise<void> => {
  };

  const sendTyping = () => {
    if (wsConnected) {
      supportWebSocket.sendTyping();
    }
  };

  const stopTyping = () => {
    if (wsConnected) {
      supportWebSocket.stopTyping();
    }
  };

  const value = {
    activeTicket,
    tickets,
    messages,
    isConnected: wsConnected,
    isLoadingTicket,
    typingUser,
    createTicket,
    sendSupportMessage,
    closeTicket,
    joinTicket,
    leaveTicket,
    adminCloseTicket,
    loadMessages,
    sendTyping,
    stopTyping,
  };

  return (
    <LiveSupportContext.Provider value={value}>
      {children}
    </LiveSupportContext.Provider>
  );
};
